import {
  HuidigeSituatieHypotheek,
  LeningdeelDetail,
} from "../../../../.generated/rapportage/rapportagetypes";
import { formatDate, formatDuration } from "../../../formatters";
import { Column } from "../../components/BaseTable";

type HypotheekType = "voorstel" | "huidigeSituatie";

// Consolidated columns for both LeningdeelDetail and HuidigeSituatieHypotheek
export const getLeningdelenColumns = <
  T extends LeningdeelDetail | HuidigeSituatieHypotheek
>(
  type: HypotheekType
): Column<T>[] => {
  const isVoorstel = type === "voorstel";

  const columns: Column<T>[] = [
    {
      header: "Onderdeel",
      accessor: isVoorstel ? "onderdeel" : "titel",
      align: "right" as const,
    },
    {
      header: "Hypotheek op woning, rangorde",
      accessor: isVoorstel ? () => null : "hypotheekOpWoning",
    },
    {
      header: "Geldverstrekker",
      accessor: isVoorstel ? () => null : "geldverstrekker",
      align: "right" as const,
    },
    {
      header: "Geldnemer(s)",
      accessor: isVoorstel ? () => null : "geldnemers",
      align: "right" as const,
    },
    {
      header: "Leningnummer",
      accessor: isVoorstel ? "leningnummer" : () => null,
      align: "right" as const,
    },
    {
      header: "Ingangsdatum",
      accessor: "ingangsdatum",
      align: "right" as const,
      dataType: "date" as const,
    },
    {
      header: "Einddatum, looptijd",
      accessor: isVoorstel
        ? () => null
        : (row: HuidigeSituatieHypotheek) =>
            row.einddatum || row.looptijdInMaanden
              ? `${formatDate(row.einddatum)}, ${formatDuration(
                  row.looptijdInMaanden
                )}`
              : null,
      align: "right" as const,
    },
    {
      header: "Hypotheekvorm",
      accessor: "hypotheekvorm",
      align: "right" as const,
    },
    {
      header: "Hypotheekbedrag",
      accessor: isVoorstel ? "hypotheekbedrag" : "oorspronkelijkeHoofdsom",
      align: "right" as const,
      dataType: "currency" as const,
    },
    {
      header: "Gedeelte consumptief (Box 3)",
      accessor: isVoorstel ? "gedeelteConsumptief" : "waarvanInBox3",
      align: "right" as const,
      dataType: "currency" as const,
    },
    {
      header: "Looptijd lening",
      accessor: isVoorstel ? "looptijd" : () => null,
      align: "right" as const,
    },
    {
      header: "Rentepercentage",
      accessor: "rentepercentage",
      align: "right" as const,
      dataType: "percentage" as const,
    },
    {
      header: "Rentevastperiode, einddatum",
      accessor: isVoorstel
        ? "rentevastperiodeEinddatum"
        : (row: HuidigeSituatieHypotheek) =>
            `${row.rentevastperiode}, ${formatDate(
              row.rentevastperiodeEinddatum
            )}`,
      align: "right" as const,
    },
    {
      header: "Renteaftrek over",
      accessor: isVoorstel
        ? "renteaftrekOver"
        : (row) => {
            const hypotheekRow = (row as unknown) as HuidigeSituatieHypotheek;
            if (
              !hypotheekRow.renteaftrekken ||
              hypotheekRow.renteaftrekken.length === 0
            ) {
              return null;
            }
            return hypotheekRow.renteaftrekken[0]?.waarvanInBox1;
          },
      align: "right" as const,
      dataType: "currency" as const,
    },
    {
      header: "Einddatum renteaftrek",
      accessor: isVoorstel
        ? "einddatumRenteaftrek"
        : (row) => {
            const hypotheekRow = (row as unknown) as HuidigeSituatieHypotheek;
            if (
              !hypotheekRow.renteaftrekken ||
              hypotheekRow.renteaftrekken.length === 0
            ) {
              return null;
            }
            return hypotheekRow.renteaftrekken[0]?.einddatum;
          },
      align: "right" as const,
      dataType: "date" as const,
    },
  ] as Column<T>[];

  return columns;
};

// Backward compatibility wrapper for HuidigeSituatieHypotheek
export const getHypothekenColumns = (): Column<HuidigeSituatieHypotheek>[] => {
  return getLeningdelenColumns<HuidigeSituatieHypotheek>("huidigeSituatie");
};
